import React from "react";
import { useTranslation } from "react-i18next";
import {
  UpfrontPaymentButtonRow,
  UpfrontPaymentCost,
  UpfrontPaymentDashed,
  UpfrontPaymentDesktopContainer,
  UpfrontPaymentFinancialAssistance,
  UpfrontPaymentFinancialAssistanceWrapper,
  UpfrontPaymentInfoBillContent,
  UpfrontPaymentMainText,
  UpfrontPaymentReceiptTitle,
} from "./UpfrontPayment.styles";

import { useDeviceHook } from "../../utils/hooks/useDeviceHook";

import { Button } from "../shared/Button/Button";
import { getCentsToDollars, Bill } from "./Services/UpfrontPaymentService";
import { CentsConversion, PaymentPlanMinDollars, StepperIndex } from "./types";
import { UpfrontPaymentPaperStatements } from "./UpfrontPaymentPaperStatements";

export const UpfrontPaymentBillingSummary: React.FC<{
  setStep: React.Dispatch<React.SetStateAction<StepperIndex>>;
  setPaymentOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setStripeOpen: (open: boolean) => Promise<void>;
  upfrontBill: Bill;
}> = ({ setPaymentOpen, setStripeOpen, upfrontBill, setStep }) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();

  return (
    <>
      <UpfrontPaymentDesktopContainer deviceType={deviceType}>
        <UpfrontPaymentReceiptTitle>
          {t("upfrontPayment.billingSummary.title")}
        </UpfrontPaymentReceiptTitle>

        {upfrontBill && (
          <>
            <UpfrontPaymentInfoBillContent>
              <UpfrontPaymentMainText>
                {upfrontBill?.date_of_service}
              </UpfrontPaymentMainText>
              <UpfrontPaymentCost>
                {getCentsToDollars(upfrontBill?.balance)}
              </UpfrontPaymentCost>
            </UpfrontPaymentInfoBillContent>
            <UpfrontPaymentInfoBillContent>
              <UpfrontPaymentMainText>
                {t("upfrontPayment.billingSummary.totalCharges")}
              </UpfrontPaymentMainText>
              <UpfrontPaymentMainText>
                {getCentsToDollars(upfrontBill?.total_insurance_allowed_amount)}
              </UpfrontPaymentMainText>
            </UpfrontPaymentInfoBillContent>
            <UpfrontPaymentInfoBillContent>
              <UpfrontPaymentMainText>
                {upfrontBill?.insurance_adjustment_wording}
              </UpfrontPaymentMainText>
              <UpfrontPaymentMainText $color="greenDark">
                -{getCentsToDollars(upfrontBill?.total_insurance_paid_amount)}
              </UpfrontPaymentMainText>
            </UpfrontPaymentInfoBillContent>
            {Boolean(upfrontBill?.bto_discount) && (
              <UpfrontPaymentInfoBillContent>
                <UpfrontPaymentMainText>
                  {upfrontBill?.is_qualified_for_financial_assistance
                    ? t(
                        "upfrontPayment.billingSummary.financialAssistanceAdjustment",
                      )
                    : t("upfrontPayment.billingSummary.courtesyAdjustment")}
                </UpfrontPaymentMainText>
                <UpfrontPaymentMainText $color="greenDark">
                  -{getCentsToDollars(upfrontBill?.bto_discount)}
                </UpfrontPaymentMainText>
              </UpfrontPaymentInfoBillContent>
            )}
            <UpfrontPaymentInfoBillContent>
              <UpfrontPaymentMainText>
                {t("upfrontPayment.billingSummary.alreadyPaid")}
              </UpfrontPaymentMainText>
              {upfrontBill?.paid_amount ? (
                <UpfrontPaymentMainText $color="greenDark">
                  -{getCentsToDollars(upfrontBill?.paid_amount)}
                </UpfrontPaymentMainText>
              ) : (
                <UpfrontPaymentMainText>
                  {getCentsToDollars(upfrontBill?.paid_amount)}
                </UpfrontPaymentMainText>
              )}
            </UpfrontPaymentInfoBillContent>
            <UpfrontPaymentDashed />
            <UpfrontPaymentInfoBillContent style={{ marginTop: "10px" }}>
              <UpfrontPaymentMainText>
                {t("upfrontPayment.billingSummary.balanceDue")}
              </UpfrontPaymentMainText>
              <UpfrontPaymentMainText>
                {getCentsToDollars(upfrontBill?.balance)}
              </UpfrontPaymentMainText>
            </UpfrontPaymentInfoBillContent>
            {upfrontBill.portal_bill_status === "outstanding" && (
              <>
                {" "}
                <UpfrontPaymentButtonRow>
                  <Button
                    type="submit"
                    label={t("upfrontPayment.actions.payAmount", {
                      amount: getCentsToDollars(upfrontBill?.balance),
                    })}
                    size="medium"
                    onClick={() => setStripeOpen(true)}
                    backgroundColor="greenDark"
                  />
                </UpfrontPaymentButtonRow>
                {upfrontBill.balance / CentsConversion >
                  PaymentPlanMinDollars && (
                  <UpfrontPaymentButtonRow bottomRow>
                    <Button
                      type="submit"
                      label={t("upfrontPayment.actions.buildPaymentPlan")}
                      size="medium"
                      reverse
                      onClick={() => setStep(StepperIndex.PaymentPlan)}
                      backgroundColor="greenDark"
                    />
                  </UpfrontPaymentButtonRow>
                )}
              </>
            )}

            {upfrontBill.show_financial_assistance && (
              <UpfrontPaymentFinancialAssistanceWrapper
                onClick={() => setPaymentOpen(true)}
              >
                <UpfrontPaymentFinancialAssistance>
                  {t("upfrontPayment.actions.applyForFinancialAssistance")}
                </UpfrontPaymentFinancialAssistance>
              </UpfrontPaymentFinancialAssistanceWrapper>
            )}
          </>
        )}
      </UpfrontPaymentDesktopContainer>

      <UpfrontPaymentPaperStatements upfrontBill={upfrontBill} />
    </>
  );
};
